import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LOGO_W,
  LOGO_H,
  initialCrop,
  withZoom,
  moveBy,
  drawRect,
  maxZoom,
  opaqueBounds,
  type LogoCrop,
} from '../lib/logoCrop.js';

/**
 * fbs#99 (Chase 10-07): zoom and position the practice logo before it saves, like the
 * Provider Profile photo: drag (or the arrow keys) to move, the slider to zoom, Choose
 * another, Save. The frame is the stored size (600 × 200); the saved image is trimmed to the
 * logo's visible pixels, so it sits as tight on a form as before.
 *
 * Rendered in a portal above Data & Settings. It takes Escape first (so Settings stays open)
 * and keeps its own change events from reaching the Settings "unsaved changes" tracker.
 */
const WORK_MAX = 2400; // a big logo is shrunk once to a working copy so dragging stays smooth
const MAX_CHARS = 500_000; // the API's limit
const PX = 2; // the preview canvas draws at 2× for a crisp frame

export function renderLogo(src: CanvasImageSource, c: LogoCrop): string {
  const out = document.createElement('canvas');
  out.width = LOGO_W;
  out.height = LOGO_H;
  const g = out.getContext('2d');
  if (!g) throw new Error('no canvas');
  g.imageSmoothingQuality = 'high';
  const r = drawRect(c);
  g.drawImage(src, r.dx, r.dy, r.dw, r.dh);
  let canvas = out;
  const b = opaqueBounds(g.getImageData(0, 0, LOGO_W, LOGO_H).data, LOGO_W, LOGO_H);
  if (b && (b.w < LOGO_W || b.h < LOGO_H)) {
    const t = document.createElement('canvas');
    t.width = b.w;
    t.height = b.h;
    t.getContext('2d')?.drawImage(out, b.x, b.y, b.w, b.h, 0, 0, b.w, b.h);
    canvas = t;
  }
  // PNG keeps a transparent background; a photo-like logo too big as PNG becomes JPEG.
  let url = canvas.toDataURL('image/png');
  if (url.length > MAX_CHARS * 0.9) {
    const j = document.createElement('canvas');
    j.width = canvas.width;
    j.height = canvas.height;
    const jg = j.getContext('2d');
    if (jg) {
      jg.fillStyle = '#fff';
      jg.fillRect(0, 0, j.width, j.height);
      jg.drawImage(canvas, 0, 0);
    }
    url = j.toDataURL('image/jpeg', 0.9);
  }
  if (url.length > MAX_CHARS) throw new Error('too large');
  return url;
}

export function LogoCropDialog({
  image,
  onCancel,
  onSave,
  onChooseAnother,
}: {
  image: HTMLImageElement;
  onCancel: () => void;
  onSave: (dataUrl: string) => void;
  onChooseAnother: () => void;
}) {
  const work = useMemo(() => {
    const k = Math.min(1, WORK_MAX / Math.max(image.naturalWidth, image.naturalHeight));
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(image.naturalWidth * k));
    c.height = Math.max(1, Math.round(image.naturalHeight * k));
    c.getContext('2d')?.drawImage(image, 0, 0, c.width, c.height);
    return c;
  }, [image]);
  const zMax = maxZoom(image.naturalWidth, image.naturalHeight);
  const [crop, setCrop] = useState<LogoCrop>(() => initialCrop(work.width, work.height));
  const [err, setErr] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const g = canvasRef.current?.getContext('2d');
    if (!g) return;
    g.clearRect(0, 0, LOGO_W * PX, LOGO_H * PX);
    g.imageSmoothingQuality = 'high';
    const r = drawRect(crop);
    g.drawImage(work, r.dx * PX, r.dy * PX, r.dw * PX, r.dh * PX);
  }, [crop, work]);

  // Escape closes this dialog only, not Data & Settings behind it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      onCancel();
    };
    document.addEventListener('keydown', onKey, true);
    stageRef.current?.focus();
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onCancel]);

  const perPx = () => LOGO_W / (stageRef.current?.clientWidth || LOGO_W);

  const save = () => {
    try {
      onSave(renderLogo(work, crop));
    } catch (e) {
      setErr(
        e instanceof Error && e.message === 'too large'
          ? 'That image is too detailed to store. Try a simpler PNG, or zoom in less.'
          : 'That image could not be saved.',
      );
    }
  };

  return createPortal(
    <div
      className="lc-backdrop"
      onClick={onCancel}
      onChange={(e) => e.stopPropagation()}
      onInput={(e) => e.stopPropagation()}
    >
      <div
        className="lc-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lc-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="lc-head">
          <h3 id="lc-title">Position the logo</h3>
          <button type="button" className="lc-x" aria-label="Close" onClick={onCancel}>
            ✕
          </button>
        </div>
        <div className="lc-body">
          <div
            className="lc-stage"
            ref={stageRef}
            tabIndex={0}
            aria-label="Logo position. Drag, or use the arrow keys, to move the logo."
            onPointerDown={(e) => {
              drag.current = { x: e.clientX, y: e.clientY };
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch {
                /* dragging works without capture */
              }
            }}
            onPointerMove={(e) => {
              if (!drag.current) return;
              const k = perPx();
              const dx = (e.clientX - drag.current.x) * k;
              const dy = (e.clientY - drag.current.y) * k;
              drag.current = { x: e.clientX, y: e.clientY };
              setCrop((c) => moveBy(c, dx, dy));
            }}
            onPointerUp={() => (drag.current = null)}
            onPointerCancel={() => (drag.current = null)}
            onKeyDown={(e) => {
              const d = (
                {
                  ArrowLeft: [-1, 0],
                  ArrowRight: [1, 0],
                  ArrowUp: [0, -1],
                  ArrowDown: [0, 1],
                } as Record<string, [number, number]>
              )[e.key];
              if (!d) return;
              e.preventDefault();
              setCrop((c) => moveBy(c, d[0] * LOGO_W * 0.02, d[1] * LOGO_H * 0.04));
            }}
          >
            <canvas ref={canvasRef} width={LOGO_W * PX} height={LOGO_H * PX} />
          </div>
          <div className="lc-zoom">
            <span>Zoom</span>
            <input
              type="range"
              min={1}
              max={zMax}
              step={0.01}
              value={crop.zoom}
              disabled={zMax < 1.01}
              aria-label="Zoom"
              onChange={(e) => setCrop((c) => withZoom(c, Number(e.target.value)))}
            />
          </div>
          <div className="lc-note">
            {zMax < 1.01
              ? 'This image is too small to zoom and stay sharp. Drag to move it, or choose a larger PNG.'
              : 'Drag to move, and zoom to crop. The frame is how the logo sits at the top of your forms.'}
          </div>
          {err && (
            <div className="lc-err" role="alert">
              {err}
            </div>
          )}
        </div>
        <div className="lc-foot">
          <button type="button" className="btn ghost" onClick={onChooseAnother}>
            Choose another
          </button>
          <span className="lc-spacer" />
          <button type="button" className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="btn primary" onClick={save}>
            Save logo
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
